import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"

interface DropdownMenuProps {
  children: React.ReactNode
}

const DropdownMenu = ({ children }: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [position, setPosition] = React.useState({ top: 0, left: 0 })
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  
  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      // Calculer la position absolue du trigger
      const rect = triggerRef.current.getBoundingClientRect()
      const newPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - 192 // 192px = min-w-[12rem]
      }
      setPosition(newPosition)
    }
    
    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)
  }
  
  const handleClose = () => {
    setIsOpen(false)
  }
  
  // Fermer le menu quand on clique en dehors
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && triggerRef.current && menuRef.current) {
        const target = event.target as Node
        // Ne fermer que si le clic n'est ni sur le trigger ni sur le menu
        if (!triggerRef.current.contains(target) && !menuRef.current.contains(target)) {
          setIsOpen(false)
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])
  
  return (
    <div className="relative inline-block" ref={triggerRef}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === DropdownMenuTrigger) {
            return React.cloneElement(child as React.ReactElement<any>, {
              onClick: handleToggle,
            })
          }
          if (child.type === DropdownMenuContent) {
            return React.cloneElement(child as React.ReactElement<any>, {
              isOpen,
              onClose: handleClose,
              position,
              menuRef,
            })
          }
        }
        return child
      })}
    </div>
  )
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode
  asChild?: boolean
  onClick?: () => void
  className?: string
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuTriggerProps
>(({ children, asChild, onClick, className, ...props }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: onClick,
      ref: ref,
      ...props,
    } as any)
  }
  
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={className}
      {...props}
    >
      {children}
    </button>
  )
})
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

interface DropdownMenuContentProps {
  children: React.ReactNode
  className?: string
  isOpen?: boolean
  onClose?: () => void
  position?: { top: number; left: number }
  menuRef?: React.RefObject<HTMLDivElement>
}

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  DropdownMenuContentProps
>(({ children, className, isOpen, onClose, position, menuRef, ...props }, ref) => {
  if (!isOpen || !position) return null
  
  const content = (
    <div
      ref={(node) => {
        // Gérer la ref passée en prop
        if (ref) {
          if (typeof ref === 'function') {
            ref(node)
          } else {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
          }
        }
        // Gérer notre ref interne
        if (menuRef && menuRef.current !== node) {
          (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }}
      className={cn(
        "fixed min-w-[12rem] overflow-hidden rounded-md border bg-white p-1 text-gray-900 shadow-lg z-[99999]",
        className
      )}
      style={{
        top: position.top,
        left: position.left,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        zIndex: 99999
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === DropdownMenuItem) {
          return React.cloneElement(child as React.ReactElement<any>, {
            onClose,
          })
        }
        return child
      })}
    </div>
  )
  
  // Utiliser un portail pour afficher le menu à l'extérieur de la hiérarchie DOM
  return createPortal(content, document.body)
})
DropdownMenuContent.displayName = "DropdownMenuContent"

interface DropdownMenuItemProps {
  children: React.ReactNode
  className?: string
  onClick?: () => void
  onClose?: () => void
  disabled?: boolean
}

const DropdownMenuItem = React.forwardRef<
  HTMLDivElement,
  DropdownMenuItemProps
>(({ children, className, onClick, onClose, disabled = false, ...props }, ref) => {
  const handleClick = (e: React.MouseEvent) => {
    // Ne pas empêcher la propagation, juste stopper la propagation vers les parents
    e.stopPropagation()
    
    if (disabled) {
      return
    }
    
    if (onClick) {
      // Exécuter onClick en premier
      onClick()
    }
    
    // Fermer le menu après un petit délai pour s'assurer que onClick s'exécute
    setTimeout(() => {
      if (onClose) {
        onClose()
      }
    }, 10)
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent focus:bg-transparent',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  )
})
DropdownMenuItem.displayName = "DropdownMenuItem"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
}